import React, { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBusinessContext } from './BusinessContext';
import { canonicalBusinessSection, consumeSettingsOrigin, rememberSettingsOrigin } from '@/lib/settings-navigation';

export type BusinessSettingsSection =
  | 'business'
  | 'hours'
  | 'staff'
  | 'reviews'
  | 'integrations'
  | 'features'
  | 'danger';

function parseSection(search: string): BusinessSettingsSection {
  return canonicalBusinessSection(new URLSearchParams(search).get('section'));
}

function extractBusinessIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/settings\/business\/([^/?]+)/);
  return match ? match[1] : null;
}

interface BusinessSettingsContextType {
  isBusinessSettingsOpen: boolean;
  activeSection: BusinessSettingsSection;
  toggleBusinessSettings: () => void;
  openBusinessSettings: (section?: BusinessSettingsSection) => void;
  closeBusinessSettings: () => void;
  setActiveSection: (section: BusinessSettingsSection) => void;
}

const BusinessSettingsContext = createContext<BusinessSettingsContextType | undefined>(undefined);

export const useBusinessSettings = () => {
  const context = useContext(BusinessSettingsContext);
  if (context === undefined) {
    throw new Error('useBusinessSettings must be used within a BusinessSettingsProvider');
  }
  return context;
};

interface BusinessSettingsProviderProps {
  children: ReactNode;
}

export const BusinessSettingsProvider: React.FC<BusinessSettingsProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBusinessId } = useBusinessContext();

  const isBusinessSettingsOpen = location.pathname.startsWith('/settings/business/');
  const activeSection = isBusinessSettingsOpen ? parseSection(location.search) : 'business';

  const openBusinessSettings = useCallback((section?: BusinessSettingsSection) => {
    if (!selectedBusinessId) return;
    rememberSettingsOrigin(location.pathname, location.search);
    const sec = section || 'business';
    navigate(`/settings/business/${selectedBusinessId}?section=${sec}`);
  }, [navigate, selectedBusinessId, location.pathname, location.search]);

  const closeBusinessSettings = useCallback(() => {
    if (!location.pathname.startsWith('/settings/business/')) return;
    navigate(consumeSettingsOrigin());
  }, [navigate, location.pathname]);

  const toggleBusinessSettings = useCallback(() => {
    if (isBusinessSettingsOpen) {
      closeBusinessSettings();
    } else {
      openBusinessSettings();
    }
  }, [isBusinessSettingsOpen, closeBusinessSettings, openBusinessSettings]);

  const setActiveSection = useCallback((section: BusinessSettingsSection) => {
    // Extract the current businessId from URL to preserve it
    const urlBusinessId = extractBusinessIdFromPath(location.pathname);
    const bizId = urlBusinessId || selectedBusinessId;
    if (bizId) {
      navigate(`/settings/business/${bizId}?section=${section}`, { replace: true });
    }
  }, [navigate, location.pathname, selectedBusinessId]);

  const contextValue = useMemo(() => ({
    isBusinessSettingsOpen,
    activeSection,
    toggleBusinessSettings,
    openBusinessSettings,
    closeBusinessSettings,
    setActiveSection
  }), [isBusinessSettingsOpen, activeSection, toggleBusinessSettings, openBusinessSettings, closeBusinessSettings, setActiveSection]);

  return (
    <BusinessSettingsContext.Provider value={contextValue}>
      {children}
    </BusinessSettingsContext.Provider>
  );
};
